import { css } from 'aphrodite';
import * as React from 'react';
import { ResponsiveContainer, Tooltip, Treemap, TreemapNode } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../../helpers/chartColors';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { styleSheet } from './styles';

interface CommissionsTreemapChartProps {
	data?: {
		title: string;
		visualizationType: string;
		data: {
			id: string;
			label: string;
			count: number;
			value: number;
		}[];
	};
	isLoading?: boolean;
	className?: string;
}

export const CommissionsByOwnerTreemapChart: React.FC<CommissionsTreemapChartProps> = ({
	data,
	isLoading,
	className,
}) => {
	const chartData = React.useMemo<
		{
			name: string;
			children: {
				id: string;
				name: string;
				label: string;
				count: number;
				value: number;
				color: string;
			}[];
		}[]
	>(() => {
		if (!data?.data || data.data.length === 0) {
			return [{ name: 'Commissions', children: [] }];
		}

		// Sort by value (descending) and assign colors

		const children = [...data.data]
			.sort((a, b) => b.value - a.value)
			.map(item => {
				const color = mainChartColor();
				return {
					...item,
					name: item.label,
					color,
				};
			});

		return [
			{
				name: 'Commissions',
				children,
			},
		];
	}, [data]);

	const totalCommissions = React.useMemo(() => {
		return chartData[0]?.children?.reduce((sum, item) => sum + item.value, 0) || 0;
	}, [chartData]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0 || chartData[0]?.children.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No commissions data available</div>
			</div>
		);
	}

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const tooltipData = payload[0].payload;
			const percentage = totalCommissions > 0 ? ((tooltipData.value / totalCommissions) * 100).toFixed(1) : '0';
			return (
				<div className={css(styleSheet.tooltip)}>
					<p className={css(styleSheet.tooltipLabel)}>{tooltipData.label}</p>
					<p className={css(styleSheet.tooltipValue)}>Commissions: {formatCurrency(tooltipData.value)}</p>
					<p className={css(styleSheet.tooltipValue)}>Count: {tooltipData.count}</p>
					<p className={css(styleSheet.tooltipValue)}>Percentage: {percentage}%</p>
				</div>
			);
		}
		return null;
	};

	const CustomContent = (props: TreemapNode) => {
		const { x, y, width, height, name, depth } = props;

		if (width <= 0 || height <= 0) {
			return null;
		}

		// Access value and color directly from props (Recharts spreads the data object into props)
		const value = (props as any).value;
		const fillColor = (props as any).color || THEME.colors.chart.levBlue;

		// Render root node with transparent fill (depth === 0)
		if (depth === 0) {
			return <rect x={x} y={y} width={width} height={height} fill='transparent' stroke='none' />;
		}

		// Only render content for leaf nodes (depth === 1)
		if (value === undefined || value === null) {
			return null;
		}

		const percentage = totalCommissions > 0 ? ((value / totalCommissions) * 100).toFixed(1) : '0';

		return (
			<g>
				<rect
					x={x}
					y={y}
					width={width}
					height={height}
					fill={fillColor}
					stroke={THEME.colors.background.light}
					strokeWidth={2}
					style={{
						cursor: 'pointer',
					}}
				/>
				{width > 80 && height > 40 && name && (
					<text
						x={x + width / 2}
						y={y + height / 2}
						textAnchor='middle'
						stroke='transparent'
						fill='#fff'
						fontSize={12}
						fontWeight={600}
					>
						{name.length > 15 ? `${name.substring(0, 15)}...` : name}
					</text>
				)}
				{width > 80 && height > 60 && value !== undefined && (
					<text
						stroke='transparent'
						x={x + width / 2}
						y={y + height / 2 + 15}
						textAnchor='middle'
						fill='#fff'
						fontSize={10}
					>
						{formatCurrency(value)}
					</text>
				)}
				{width > 80 && height > 80 && (
					<text
						stroke='transparent'
						x={x + width / 2}
						y={y + height / 2 + 30}
						textAnchor='middle'
						fill='#fff'
						fontSize={10}
					>
						{percentage}%
					</text>
				)}
			</g>
		);
	};

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h3 className={css(styleSheet.title)}>{data?.title || 'Commissions by Policy Owner (Treemap)'}</h3>
			<ResponsiveContainer width='100%' height={400}>
				<Treemap
					data={chartData}
					dataKey='value'
					stroke={THEME.colors.border.main}
					fill={THEME.colors.chart.levBlue}
					content={CustomContent}
				>
					<Tooltip content={CustomTooltip} />
				</Treemap>
			</ResponsiveContainer>
		</div>
	);
};
